export interface IGetEnvOptions<T = Record<string, any>> {
  env?: T | NodeJS.ProcessEnv;
  logger?: any;
  defaultValue?: string | number;
  suppressWarning?: boolean;
}

export enum EEnvKeys {
  PRODUCTION = 'production',
  DEVELOPMENT = 'development',
  TEST = 'test',
}

export const getEnv = (
  key: string,
  opts?: IGetEnvOptions,
): string | number | undefined => {
  try {
    const target = opts?.env ?? process.env;
    const value = target?.[key];

    if (value === undefined || value === null || value === '') {
      const msg = `Environment variable ${key} is not set.`;

      if (!opts?.suppressWarning) {
        opts?.logger?.warn?.(msg);
      }

      if (opts?.defaultValue !== undefined) {
        if (!opts?.suppressWarning) {
          opts?.logger?.log?.(
            `Using default value for ${key}: ${opts.defaultValue}`,
          );
        }
        return opts.defaultValue;
      }

      if (!opts?.suppressWarning) {
        opts?.logger?.warn?.(Error(msg));
      }
    }

    return value;
  } catch (error) {
    opts?.logger?.error?.(
      `Error retrieving environment variable ${key}:`,
      error,
    );
    throw error;
  }
};

const isEnv = (
  value: EEnvKeys,
  env?: IGetEnvOptions['env'],
  logger?: any,
): boolean =>
  getEnv('MODE', { env, logger, suppressWarning: true }) === value ||
  getEnv('NODE_ENV', { env, logger, suppressWarning: true }) === value;

export const isProd = (env?: IGetEnvOptions['env'], logger?: any): boolean =>
  isEnv(EEnvKeys.PRODUCTION, env, logger);

export const isDev = (env?: IGetEnvOptions['env'], logger?: any): boolean =>
  isEnv(EEnvKeys.DEVELOPMENT, env, logger);

export const isTest = (env?: IGetEnvOptions['env'], logger?: any): boolean =>
  isEnv(EEnvKeys.TEST, env, logger);
